import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { awards, sensamakara } from '../data/profileData';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

function SensamakaraPanel({ onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  React.useEffect(() => {
    closeRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll('button, a[href]');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Sensamakara project detail"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6"
    >
      {/* Backdrop */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 bg-stone-900/55 cursor-default"
        onClick={onClose}
        aria-label="Close detail panel"
        tabIndex={0}
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="relative z-10 bg-white w-full md:max-w-2xl max-h-[85vh] overflow-y-auto rounded-t-2xl md:rounded-xl shadow-2xl border border-stone-200/80"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-100 px-5 sm:px-6 pt-5 pb-4 flex items-start justify-between z-10">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500 mb-1">
              Project · Team, Universitas Indonesia
            </p>
            <h3 className="text-xl font-serif text-stone-900">Sensamakara</h3>
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            className="text-stone-500 hover:text-stone-900 transition-colors ml-4 min-w-11 min-h-11 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded"
            aria-label="Close Sensamakara panel"
          >
            <span aria-hidden="true" className="text-xl leading-none">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-5 sm:space-y-6 pb-8">
          {/* Description */}
          <p className="text-[13px] sm:text-[14px] text-stone-600 leading-relaxed">
            {sensamakara.description}
          </p>

          {/* Technical detail */}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-2">How it works</p>
            <p className="text-[13px] text-stone-600 leading-relaxed">{sensamakara.technicalDetail}</p>
          </div>

          {/* Attribution note */}
          <p className="text-[12px] text-stone-500 italic">{sensamakara.attribution}</p>

          {/* Milestones */}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 mb-3">Timeline</p>
            <div className="space-y-4">
              {sensamakara.milestones.map((m) => (
                <div key={m.year} className="flex gap-4 items-start">
                  <span className="font-mono text-[10px] text-stone-500 w-10 flex-shrink-0 pt-0.5">
                    {m.year}
                  </span>
                  <div className="flex-grow">
                    <p className="text-sm text-stone-700 font-medium">{m.label}</p>
                    {m.awardRef === 'GEMASTIK' && (
                      <p className="text-[12px] text-stone-500 mt-0.5">
                        Bronze medal, Smart Device, Embedded System &amp; IoT category. Pusat Prestasi Nasional, Kemendikbudristek.
                      </p>
                    )}
                    {m.awardRef === 'JDIE2023' && (
                      <p className="text-[12px] text-stone-500 mt-0.5">
                        Selected for exhibition at the Japan Design, Idea and Invention Expo (JDIE) 2023 through the organizers&apos; professional curation process.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="pt-2 border-t border-stone-100 flex flex-col gap-3">
            <a
              href={sensamakara.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[12px] font-mono text-stone-600 hover:text-stone-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded"
            >
              <span className="border-b border-stone-300 hover:border-stone-900 pb-px">Project repository on GitHub</span>
              <span aria-hidden="true">↗</span>
            </a>
            <a
              href={awards.find((a) => a.id === 'GEMASTIK')?.tempoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[12px] font-mono text-stone-600 hover:text-stone-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded"
            >
              <span className="border-b border-stone-300 hover:border-stone-900 pb-px">Read the coverage on Tempo</span>
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function Awards() {
  const [sensamakaraOpen, setSensamakaraOpen] = useState(false);
  const [creativesOpen, setCreativesOpen] = useState(false);
  const [activeSensamakaraTab, setActiveSensamakaraTab] = useState('pipeline');
  const [simStep, setSimStep] = useState(0);
  const [isSimulating, setIsSimulating] = useState(false);
  const projectTriggerRef = useRef(null);

  const closeProject = React.useCallback(() => {
    setSensamakaraOpen(false);
    window.requestAnimationFrame(() => projectTriggerRef.current?.focus());
  }, []);

  // Keyboard: close on Escape
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && !sensamakaraOpen) setCreativesOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [sensamakaraOpen]);

  const runSimulation = () => {
    if (isSimulating) return;
    setIsSimulating(true);
    setSimStep(1);
    setTimeout(() => setSimStep(2), 600);
    setTimeout(() => setSimStep(3), 1200);
    setTimeout(() => {
      setSimStep(0);
      setIsSimulating(false);
    }, 2800);
  };

  const academicAwards = awards.filter((a) =>
    ['UDAYANA-2020', 'UDAYANA-IT-2019', 'UDAYANA-2019', 'PNB-2018'].includes(a.id)
  );
  const fujifilm = awards.find((a) => a.id === 'FUJIFILM');

  return (
    <>
      <section id="section-awards" className="snap-start min-h-screen bg-stone-50 w-full px-4 sm:px-5 py-10 sm:py-12 md:p-20">
        <div className="max-w-6xl mx-auto pt-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {/* Section header */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row sm:items-baseline justify-between border-b border-stone-900 pb-4 mb-8 md:mb-10 gap-2"
            >
              <div>
                <h2 className="text-xl sm:text-2xl font-serif italic text-stone-900">
                  Honors &amp; Awards
                </h2>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">
                Competitions, Research &amp; Exhibitions
              </span>
            </motion.div>

            {/* GEMASTIK + Sensamakara - Featured Exhibition Specimen */}
            <motion.div variants={itemVariants} className="mb-8 sm:mb-10 md:mb-12">
              <div className="bg-white border border-stone-200/90 border-l-4 border-l-stone-900 p-5 sm:p-7 md:p-8 shadow-sm">
                
                {/* Top Badge & Metadata */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 pb-3 mb-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-800 bg-yellow-100/70 border border-yellow-300 px-2 py-0.5 rounded-sm font-medium">
                      Bronze Medalist · GEMASTIK XV
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-wider text-stone-500 bg-stone-100 px-2 py-0.5 rounded-sm">
                      Smart Device, Embedded System &amp; IoT
                    </span>
                  </div>
                  <span className="font-mono text-[9.5px] text-stone-400">
                    Nov 2022 · Kemendikbudristek
                  </span>
                </div>

                {/* Title & Core Narrative */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  <div className="lg:col-span-7">
                    <h3 className="text-lg sm:text-xl font-semibold text-stone-900 leading-snug mb-1.5">
                      Sensamakara: Assistive Communication Platform
                    </h3>
                    <p className="text-[12px] text-stone-500 font-mono mb-3">
                      Universitas Indonesia · Pusat Prestasi Nasional
                    </p>
                    <p className="text-[13px] sm:text-[13.5px] text-stone-600 leading-relaxed font-light mb-4">
                      Developed as part of a Universitas Indonesia engineering team, Sensamakara is an assistive communication system engineered for individuals with motor disabilities and conditions such as ALS, translating eye-blink patterns into speech and interactive actions.
                    </p>

                    {/* JDIE Exhibition Milestone */}
                    <div className="bg-stone-50 border border-stone-200/80 p-3 rounded-sm mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-[9px] uppercase tracking-wider text-stone-700 font-semibold">
                          Selected for Exhibition · JDIE 2023, Tokyo
                        </span>
                        <span className="font-mono text-[8.5px] text-stone-400">
                          International Curation
                        </span>
                      </div>
                      <p className="text-[12px] text-stone-500 leading-relaxed">
                        Selected through professional curation by the Japan Design, Idea and Invention Expo organizers for international exhibition in Tokyo.
                      </p>
                    </div>

                    {/* Action Links */}
                    <div className="flex flex-wrap gap-3 pt-1">
                      <button
                        ref={projectTriggerRef}
                        onClick={() => setSensamakaraOpen(true)}
                        className="min-h-10 text-[10.5px] font-mono uppercase tracking-wider text-stone-900 bg-stone-100 hover:bg-stone-900 hover:text-white border border-stone-300 hover:border-stone-900 px-3.5 py-1.5 transition-colors duration-200 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded-sm"
                      >
                        Project Dossier
                      </button>
                      <a
                        href={awards.find((a) => a.id === 'GEMASTIK')?.tempoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-h-10 inline-flex items-center justify-center text-[10.5px] font-mono uppercase tracking-wider text-stone-600 hover:text-stone-900 border border-stone-200 hover:border-stone-900 px-3.5 py-1.5 transition-colors text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded-sm"
                      >
                        Tempo Coverage ↗
                      </a>
                      <a
                        href={sensamakara.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-h-10 inline-flex items-center justify-center text-[10.5px] font-mono uppercase tracking-wider text-stone-600 hover:text-stone-900 border border-stone-200 hover:border-stone-900 px-3.5 py-1.5 transition-colors text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded-sm"
                      >
                        GitHub Repository ↗
                      </a>
                    </div>
                  </div>

                  {/* Interactive Architecture & Simulation Specimen */}
                  <div className="lg:col-span-5 bg-stone-50/90 border border-stone-200 p-3.5 sm:p-4 rounded-sm">
                    <div className="flex items-center justify-between border-b border-stone-200 pb-2 mb-3">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-500 font-medium">
                        Architecture Specimen
                      </span>
                      <button
                        onClick={runSimulation}
                        disabled={isSimulating}
                        className="font-mono text-[8.5px] uppercase tracking-wider text-stone-700 hover:text-stone-900 bg-white border border-stone-300 hover:border-stone-900 px-2 py-0.5 rounded-sm transition-colors disabled:opacity-50"
                      >
                        {isSimulating ? 'Simulating...' : 'Test Simulation ▶'}
                      </button>
                    </div>

                    {/* Specimen Tabs */}
                    <div className="grid grid-cols-2 gap-1.5 mb-3" role="tablist">
                      {[
                        { id: 'pipeline', label: '01 / Vision & EAR' },
                        { id: 'nlp', label: '02 / Trie & TTS' },
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          role="tab"
                          aria-selected={activeSensamakaraTab === tab.id}
                          onClick={() => setActiveSensamakaraTab(tab.id)}
                          className={`text-left px-2.5 py-1.5 border text-[10px] sm:text-[10.5px] font-mono transition-colors rounded-sm ${
                            activeSensamakaraTab === tab.id
                              ? 'bg-stone-900 text-stone-50 border-stone-900'
                              : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-100'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Tab Content */}
                    <div className="bg-white border border-stone-200/80 p-3 rounded-sm min-h-[140px] flex flex-col justify-between">
                      {activeSensamakaraTab === 'pipeline' ? (
                        <div className="space-y-1.5">
                          <p className="text-[11.5px] font-medium text-stone-900">
                            Facial Landmark Eye Aspect Ratio (EAR)
                          </p>
                          <p className="text-[11px] text-stone-600 leading-relaxed font-light">
                            MediaPipe and OpenCV track 6 eye coordinates. When EAR drops below threshold for a defined window, a deliberate blink is recognized while filtering natural ocular twitches.
                          </p>
                          <div className="pt-1 flex flex-wrap gap-1 font-mono text-[8.5px] text-stone-500">
                            <span className="bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded-xs">EAR &lt; 0.20 Threshold</span>
                            <span className="bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded-xs">Debounce Filter</span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <p className="text-[11.5px] font-medium text-stone-900">
                            Fuzzy Trie Dictionary &amp; Speech Output
                          </p>
                          <p className="text-[11px] text-stone-600 leading-relaxed font-light">
                            Selected character streams query a stored in-memory Trie dictionary. Approximate matching surfaces words with minimal keystrokes, dispatched to text-to-speech audio synthesis.
                          </p>
                          <div className="pt-1 flex flex-wrap gap-1 font-mono text-[8.5px] text-stone-500">
                            <span className="bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded-xs">Prefix Search</span>
                            <span className="bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded-xs">TTS Audio Engine</span>
                          </div>
                        </div>
                      )}

                      {/* Live Simulation Indicator Bar */}
                      <div className="mt-2 pt-2 border-t border-stone-100 flex items-center justify-between text-[10px] font-mono text-stone-500">
                        <span>Sim Status:</span>
                        <span className="font-semibold text-stone-800">
                          {simStep === 0 && 'Ready for input'}
                          {simStep === 1 && '● Eye Blink Registered'}
                          {simStep === 2 && '● Trie Suggestion: "HELLO"'}
                          {simStep === 3 && '● TTS Synthesized: [Audio Out]'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </motion.div>

            {/* Academic Quiz Competitions Archive */}
            <motion.div variants={itemVariants} className="mb-8 sm:mb-10">
              <div className="flex items-baseline justify-between border-b border-stone-200 pb-2.5 mb-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500">
                  Academic Competitions Archive
                </p>
                <span className="font-mono text-[8.5px] text-stone-400">
                  Electrical, IT &amp; Engineering Quizzes
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {academicAwards.map((a) => (
                  <div
                    key={a.id}
                    className="border border-stone-200 bg-white p-3.5 rounded-sm hover:border-stone-400 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-[9px] uppercase tracking-wider text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded-xs">
                        {a.date.slice(-4)}
                      </span>
                      <span className="font-mono text-[8.5px] text-stone-400">
                        {a.issuer}
                      </span>
                    </div>
                    <p className="text-xs sm:text-[13px] font-semibold text-stone-900 leading-snug mb-1">
                      {a.title}
                    </p>
                    <p className="text-[11px] text-stone-500 font-mono">
                      {a.issuer}{a.affiliation ? ` · ${a.affiliation}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Creative Background - Collapsible Editorial Darkroom Slide */}
            <motion.div variants={itemVariants} className="border-t border-stone-300">
              <button
                onClick={() => setCreativesOpen((v) => !v)}
                aria-expanded={creativesOpen}
                className="flex items-center justify-between gap-3 text-left w-full min-h-14 group focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 py-3"
              >
                <div>
                  <span className="font-serif italic text-lg text-stone-800 group-hover:text-stone-900 transition-colors block">
                    Creative Background &amp; Visual Arts
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-stone-400 mt-0.5 block">
                    Exhibitions &amp; Photography Honors
                  </span>
                </div>
                <span className="text-stone-600 group-hover:text-stone-900 transition-colors font-mono text-lg w-9 h-9 flex items-center justify-center border border-stone-300 rounded-sm" aria-hidden="true">
                  {creativesOpen ? '−' : '+'}
                </span>
              </button>

              <AnimatePresence>
                {creativesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="mt-2 border-t border-stone-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
                      {/* Fujifilm */}
                      <div className="border border-stone-200 bg-white p-3.5 rounded-sm">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-mono text-[9px] uppercase tracking-wider text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded-xs">
                            Apr 2021
                          </span>
                          <span className="font-mono text-[8.5px] text-stone-400">
                            Fujifilm Indonesia
                          </span>
                        </div>
                        <p className="text-xs sm:text-[13px] font-semibold text-stone-900 mb-1">
                          {fujifilm?.title}
                        </p>
                        <p className="text-[11px] text-stone-500 leading-relaxed font-light">
                          Straight-out-of-camera street &amp; documentary photography selection.
                        </p>
                      </div>

                      {/* Photo exhibition publication */}
                      <div className="border border-stone-200 bg-white p-3.5 rounded-sm">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-mono text-[9px] uppercase tracking-wider text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded-xs">
                            Feb 2021
                          </span>
                          <span className="font-mono text-[8.5px] text-stone-400">
                            STIKI Denpasar
                          </span>
                        </div>
                        <p className="text-xs sm:text-[13px] font-semibold text-stone-900 mb-1">
                          Sanur Serene
                        </p>
                        <p className="text-[11px] text-stone-500 leading-relaxed font-light">
                          Artspirasi VI Photo Exhibition selection capturing morning coastal atmospheres.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Sensamakara modal panel with AnimatePresence */}
      <AnimatePresence>
        {sensamakaraOpen && (
          <SensamakaraPanel
            onClose={closeProject}
          />
        )}
      </AnimatePresence>
    </>
  );
}
